import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { APP_SEMANTIC } from './colors';
import { ContainerStatus } from '../types/container.type';

/** Neutral / unknown status */
export const STATUS_NEUTRAL = '#8B95A5';

/** Maintenance & special operational states */
export const STATUS_MAINTENANCE = '#7B5EA7';

/** Container status colours — fixed across light/dark themes. */
export const CONTAINER_STATUS_COLORS: Record<ContainerStatus, string> = {
  [ContainerStatus.PENDING]: STATUS_NEUTRAL,
  [ContainerStatus.EMPTY]: APP_SEMANTIC.success.main,
  [ContainerStatus.AWAITING_PICKUP]: APP_SEMANTIC.warning.main,
  [ContainerStatus.READY_FOR_PICKUP]: APP_SEMANTIC.info.main,
  [ContainerStatus.MAINTENANCE]: STATUS_MAINTENANCE,
  [ContainerStatus.INACTIVE]: APP_SEMANTIC.error.main,
};

/** Fleet / truck colours by operational status. */
export const TRUCK_STATUS_COLORS: Record<string, string> = {
  available: APP_SEMANTIC.success.main,
  in_transit: APP_SEMANTIC.info.main,
  loading: APP_SEMANTIC.warning.main,
  unloading: APP_SEMANTIC.warning.main,
  maintenance: APP_SEMANTIC.error.main,
  unavailable: STATUS_NEUTRAL,
};

/** @deprecated Use TRUCK_STATUS_COLORS */
export const TRUCK_MARKER_COLORS = TRUCK_STATUS_COLORS;

/** Shared operational status palette (pickup orders, routes, sensors, processing…). */
export const OPERATIONAL_STATUS_COLORS: Record<string, string> = {
  pending: APP_SEMANTIC.warning.main,
  awaiting_pickup: APP_SEMANTIC.warning.main,
  active: APP_SEMANTIC.success.main,
  available: APP_SEMANTIC.success.main,
  empty: APP_SEMANTIC.success.main,
  completed: APP_SEMANTIC.success.main,
  passed: APP_SEMANTIC.success.main,
  in_transit: APP_SEMANTIC.info.main,
  in_progress: APP_SEMANTIC.info.main,
  ready_for_pickup: APP_SEMANTIC.info.main,
  loading: APP_SEMANTIC.warning.main,
  unloading: APP_SEMANTIC.warning.main,
  warning: APP_SEMANTIC.warning.main,
  maintenance: STATUS_MAINTENANCE,
  inactive: STATUS_NEUTRAL,
  unavailable: STATUS_NEUTRAL,
  cancelled: APP_SEMANTIC.error.main,
  canceled: APP_SEMANTIC.error.main,
  error: APP_SEMANTIC.error.main,
  failed: APP_SEMANTIC.error.main,
};

export function normalizeStatusKey(status: string): string {
  return status.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function getContainerStatusColor(status: ContainerStatus): string {
  return CONTAINER_STATUS_COLORS[status] ?? STATUS_NEUTRAL;
}

export function getOperationalStatusColor(status: string | null | undefined): string {
  if (!status) return STATUS_NEUTRAL;
  const key = normalizeStatusKey(status);
  const containerKey = Object.values(ContainerStatus).find((v) => v === key);
  if (containerKey) {
    return CONTAINER_STATUS_COLORS[containerKey as ContainerStatus];
  }
  return OPERATIONAL_STATUS_COLORS[key] ?? TRUCK_STATUS_COLORS[key] ?? STATUS_NEUTRAL;
}

export function getPriorityColor(priority: string | null | undefined): string {
  switch (normalizeStatusKey(priority ?? '')) {
    case 'urgent':
      return APP_SEMANTIC.error.main;
    case 'high':
      return APP_SEMANTIC.warning.main;
    case 'medium':
    case 'normal':
      return APP_SEMANTIC.info.main;
    case 'low':
      return APP_SEMANTIC.success.main;
    default:
      return STATUS_NEUTRAL;
  }
}

/** Soft status chip — consistent tint + border on every page. */
export function statusChipSx(color: string, size: 'sm' | 'md' = 'md'): SxProps<Theme> {
  const height = size === 'sm' ? 24 : 26;
  const fontSize = size === 'sm' ? '0.75rem' : '0.8125rem';
  return {
    height,
    fontSize,
    fontWeight: 600,
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.25)}`,
  };
}
